import { styled } from 'styled-components'
import WizrFlexLayout from '../WizrFlexLayout'
import WizrInput from '../WizrInput'
import WizrFlexSpacing from '../WizrFlexSpacing'
import WizrButton from '../WizrButton'
import { Form, Select, DatePicker } from 'antd'
import WizrText from '../WizrText'
import { useEffect, useState } from 'react'
import WizrDropDown from '../WizrDropDown'
import FilterPopover from '../popovers/alertsFilterPopover'
import { useControlRoomFilterSelect } from '../../redux/selectors/content'
import { dispatchGetColumnValuesForFilter, dispatchCreateAlert, dispatchUpdateAlert, dispatchGetAlertsForControlRoom } from '../../redux/actions/content'
import { 
  scheduleIntervalOptions,
  monthOptions,
  dayOfMonthOptions,
  dayOfWeekOptions,
  hourOptions,
  minuteOptions,
  parseCronExpression,
  generateCronExpression
} from '../../utils'

const StyledContainer = styled(WizrFlexLayout)`
  padding: 0px 32px 32px 32px;
  width: 100%;
  max-height: 800px;
  overflow: auto;
`

const StyledForm = styled(Form)`
  width: 100%;
  max-width: 480px;
  gap: 10px;
`

const StyledDropDown = styled(WizrDropDown)`
  height: 50px;
  .ant-select-selector {
    padding-top: 10px !important;
    border-radius:16px;
    height: 50px !important;
    background-color: #f2f4f9 !important;
    .ant-select-selection-placeholder {
      margin-top: 10px;
    }
  }
`

const StyledWizrTagInput = styled(Select)`
  /* Add your custom styles here */
  /* Target the root ant-select class to override default height */
  &.ant-select {
    height: auto !important;
    min-height: 50px !important;
  }
  
  &.ant-select-single {
    border-radius: 16px;
    height: auto !important;
    min-height: 50px !important;
  }
  
  &.ant-select-multiple {
    height: auto !important;
    min-height: 50px !important;
  }
  
  .ant-select-selection-placeholder {
    text-align: left;
    line-height: 1.4 !important;
    margin-top: 0 !important;
    padding-top: 0 !important;
  }
  width: 100%;
  .ant-select-selector {
    min-height: 50px !important;
    height: auto !important;
    background: #f2f4f9 !important;
    border-radius: 16px !important;
    padding: 10px 20px !important;
    overflow: visible !important;
    display: flex !important;
    align-items: center !important;
    line-height: 1.4 !important;
  }
  
  /* Single selection mode styling */
  &.ant-select-single .ant-select-selector {
    overflow: visible !important;
  }
  
  &.ant-select-single .ant-select-selection-item {
    background: white !important;
    border-radius: 20px !important;
    border: 1px solid black !important;
    padding: 6px 12px !important;
    margin-right: 8px !important;
    flex: none !important;
    width: auto !important;
    max-width: none !important;
    overflow: visible !important;
    white-space: nowrap !important;
    line-height: 1.4 !important;
    height: auto !important;
  }
  
  /* Multiple selection mode styling */
  &.ant-select-multiple .ant-select-selection-item {
    background: white !important;
    border-radius: 20px !important;
    border: 1px solid black !important;
    padding: 6px 12px !important;
    margin-right: 8px !important;
    flex: none !important;
    width: auto !important;
    max-width: none !important;
    overflow: visible !important;
    white-space: nowrap !important;
    line-height: 1.4 !important;
    height: auto !important;
  }
`

const InputContainer = styled(WizrFlexLayout)`
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  background-color: #fff;
`

const StyledWizrInput = styled(WizrInput)`
  background: #F2F4F9;
  border-radius: 16px;
  height: 50px;
`

const FiltersContainer = styled(WizrFlexLayout)`
  justify-content: flex-start;
  flex-direction: row;
  gap: 10px;
  margin: 16px 0px;
  max-height: 30px;
  background: #FFF;
`

const FilterFormItem = styled(Form.Item)`
  margin-bottom: 16px;
`

const CustomDatePickerContainer = styled.div`
  margin-top: 32px;
`

const StyledDateRangePicker = styled(DatePicker.RangePicker)`
  width: 100%;
`

const FormActionsContainer = styled(Form.Item)`
  margin-top: 32px;
`

const CancelButton = styled(WizrButton)`
  border-radius: 20px;
  padding: 8px 24px;
  border: 1px solid #E5E5E5;
  height: 40px;
  min-width: 120px;
`

const SubmitButton = styled(WizrButton)`
  border-radius: 20px;
  padding: 8px 24px;
  background: #000;
  height: 40px;
`

const StyledContainerWithBackground = styled(StyledContainer)`
  background: #FFF;
`

const FormActionsFlexSpacing = styled(WizrFlexSpacing)`
  justify-content: flex-end;
  flex-direction: row;
  background: #FFF;
`

interface IUploadConfluenceForm {
  onFormSubmission: any
  onCancel: any
  action?: string
  details?: any
  alert_id?: any
  chart_id?: any
}

const NewAlertForm = ({ onFormSubmission, onCancel, action = "create", details, alert_id, chart_id }: IUploadConfluenceForm): JSX.Element => {

  const [form] = Form.useForm()
  const options: any = []

  const filterData = useControlRoomFilterSelect()

  // Filter options based on discover_enabled true
  const columnOptions = filterData?.columns
    ?.filter((item: any) => item.discover_enabled)
    .map((item: any) => ({
      label: item.name.replace(/_/g, ' '), // Replace underscores with spaces for better readability
      value: item.name
    })) || [];


  const dropDownConverterIntegrations = (value: any[]): { label: string; value: string }[] => {
    if (!value?.length) return [];

    // Filter for only Confluence integrations
    const confluenceIntegrations = value.filter(item =>
      item?.integration_key?.toLowerCase() === 'alertform'
    );

    return confluenceIntegrations.map((item: any) => ({
      label: item?.display_name,
      value: item.id,
    }));
  }

  const [data, setdata] = useState({
    alert_name: '',
    email: '',
    schedule: '',
    confluence_space_url: ''
  })

  const [selectedColumns, setSelectedColumns] = useState<string[]>([])

  // Schedule state
  const [scheduleInterval, setScheduleInterval] = useState('day')
  const [scheduleConfig, setScheduleConfig] = useState({
    month: 1,
    dayOfMonth: 1,
    dayOfWeek: 1,
    hour: 9,
    minute: 0
  })
  const [cronExpression, setCronExpression] = useState('* * * * *')

  // Dynamic filters state
  const [selectedFilterColumns, setSelectedFilterColumns] = useState<string[]>([])
  const [filterValues, setFilterValues] = useState<{ [key: string]: any }>({})
  const [columnOptions_Values, setColumnOptions_Values] = useState<{ [key: string]: any[] }>({})
  const [loadingColumns, setLoadingColumns] = useState<{ [key: string]: boolean }>({})
  
  // State for custom date ranges (for time-based filters)
  const [customDateRanges, setCustomDateRanges] = useState<{
    [key: string]: { start: string | null; end: string | null }
  }>({})

  // Helper function to check if a column is a time-based filter
  const isTimeBasedFilter = (columnName: string): boolean => {
    const lowerName = columnName.toLowerCase()
    return lowerName.includes('time of resolution') || 
           lowerName.includes('created date') || 
           lowerName.includes('updated date') || 
           lowerName.includes('first response time') ||
           lowerName.includes('created_date') ||
           lowerName.includes('updated_date') ||
           lowerName.includes('time_of_resolution') ||
           lowerName.includes('first_response_time')
  }

  // Helper function to create date range for predefined time periods
  const createDateRange = (selectedValue: string): { start: Date; end: Date } | null => {
    const end = new Date()
    const start = new Date()
    
    switch (selectedValue) {
      case '1 Day':
        start.setDate(start.getDate() - 1)
        break
      case '1 Week':
        start.setDate(start.getDate() - 7)
        break
      case '1 Month':
        start.setMonth(start.getMonth() - 1)
        break
      case '1 Quarter':
        start.setMonth(start.getMonth() - 3)
        break
      default:
        return null
    }
    
    return { start, end }
  }

  // Helper function to convert date range to milliseconds
  const convertToMilliseconds = (column: string, selectedValue: string): number[] | null => {
    if (selectedValue === 'Custom' && customDateRanges[column]) {
      const { start, end } = customDateRanges[column]
      if (start && end) {
        const startDate = new Date(start + 'T00:00:00')
        const endDate = new Date(end + 'T23:59:59')
        return [startDate.getTime(), endDate.getTime()]
      }
      return null
    }
    
    const dateRange = createDateRange(selectedValue)
    if (dateRange) {
      return [dateRange.start.getTime(), dateRange.end.getTime()]
    }
    
    return null
  }

  // Fetch column values when user clicks on a filter field
  const fetchColumnValues = async (columnName: string) => {
    // Don't fetch if already loading or already have data
    if (loadingColumns[columnName] || columnOptions_Values[columnName]) {
      return
    }

    setLoadingColumns(prev => ({ ...prev, [columnName]: true }))
    
    try {
      // Special case for time-based filters
      if (isTimeBasedFilter(columnName)) {
        const timeOptions = [
          { label: '1 Day', value: '1 Day' },
          { label: '1 Week', value: '1 Week' },
          { label: '1 Month', value: '1 Month' },
          { label: '1 Quarter', value: '1 Quarter' },
          { label: 'Custom', value: 'Custom' }
        ]
        
        setColumnOptions_Values(prev => ({
          ...prev,
          [columnName]: timeOptions
        }))
        setLoadingColumns(prev => ({ ...prev, [columnName]: false }))
        return
      }

      const payload = {
        column_name: columnName,
      }
      const response = await dispatchGetColumnValuesForFilter(payload)
      
      // Assuming the response contains an array of values
      // Update this based on the actual API response structure
      const values = response?.data?.values
      const formattedOptions = Array.isArray(values) 
        ? values.map((value: any) => ({
            label: typeof value === 'object' ? value.label || value.name || value.value : value,
            value: typeof value === 'object' ? value.value || value.name || value : value
          }))
        : []
      
      setColumnOptions_Values(prev => ({
        ...prev,
        [columnName]: formattedOptions
      }))
    } catch (error) {
      console.error('Error fetching column values for', columnName, ':', error)
      setColumnOptions_Values(prev => ({
        ...prev,
        [columnName]: []
      }))
    } finally {
      setLoadingColumns(prev => ({ ...prev, [columnName]: false }))
    }
  }

  // Handle filter column selection from popover
  const handleFilterColumnsChange = (columns: string[]) => {
    setSelectedFilterColumns(columns)

    // Initialize filter values for new columns
    const newFilterValues = { ...filterValues }
    columns.forEach(column => {
      if (!newFilterValues[column]) {
        newFilterValues[column] = ''
      }
    })

    // Remove values for unselected columns
    Object.keys(newFilterValues).forEach(key => {
      if (!columns.includes(key)) {
        delete newFilterValues[key]
      }
    })

    setFilterValues(newFilterValues)
  }

  // Update individual filter value
  const updateFilterValue = (column: string, value: any) => {
    
    setFilterValues({
      ...filterValues,
      [column]: value
    })
  }

  // Generate filters payload for form submission
  const generateFiltersPayload = () => {
    const conditions = selectedFilterColumns
      .filter(column => filterValues[column] && filterValues[column] !== '')
      .map(column => {
        const isTimeBased = isTimeBasedFilter(column)
        let values = Array.isArray(filterValues[column]) ? filterValues[column] : [filterValues[column]]
        
        // Handle time-based filters
        if (isTimeBased && values.length > 0) {
          const selectedValue = values[0] // Time filters typically have single selection
          const milliseconds = convertToMilliseconds(column, selectedValue)
          
          if (milliseconds) {
            return {
              type: column,
              conditionBy: '<>', // Use '<>' for time range filters
              values: milliseconds
            }
          }
          return null // Skip if no valid date range
        }
        
        // Handle regular filters
        return {
          type: column,
          conditionBy: '=', // Use "=" for regular filters
          values: values
        }
      })
      .filter(condition => condition !== null) // Remove null conditions
    
    // Return the conditions wrapped in the required array structure
    return conditions.length > 0 ? [{ conditions }] : []
  }

  // Update cron expression when schedule config or interval changes
  useEffect(() => {
    const newCron = generateCronExpression(scheduleInterval, scheduleConfig);
    setCronExpression(newCron);
    setdata({ ...data, schedule: newCron });
  }, [scheduleInterval, scheduleConfig])

  // Update data schedule when cron expression changes
  useEffect(() => {
    setdata({ ...data, schedule: cronExpression })
  }, [cronExpression])

  // Initialize form with default values for edit mode
  useEffect(() => {
    if (action === 'edit' && details) {
      // Handle both basic alert details and detailed API response
      const alertDetails = details.alert_details || details;
      const chartDetails = details.chart_details;
      
      // Parse schedule details if available
      const cronFromDetails = details.crontab || alertDetails?.crontab || details?.schedule_details?.cron || '* * * * *'
      const parsedSchedule = parseCronExpression(cronFromDetails)
      
      if (parsedSchedule) {
        setScheduleInterval(parsedSchedule.interval)
        setScheduleConfig(parsedSchedule.config)
      }
      setCronExpression(cronFromDetails)
      
      // Extract email from recipients array (if from detailed API response)
      let emailValue = ''
      if (alertDetails?.recipients && Array.isArray(alertDetails.recipients) && alertDetails.recipients.length > 0) {
        emailValue = alertDetails.recipients[0].target || ''
      } else {
        emailValue = details.email || alertDetails?.email || ''
      }
      
      // Set form data
      setdata({
        alert_name: alertDetails?.name || details.alert_name || details.name || '',
        email: emailValue,
        schedule: cronFromDetails,
        confluence_space_url: details.confluence_space_url || ''
      })
      
      // Set columns if available (from chart details or direct)
      const columnsFromChart = chartDetails?.all_columns || details.columns || alertDetails?.columns
      if (columnsFromChart && Array.isArray(columnsFromChart)) {
        setSelectedColumns(columnsFromChart)
      }
      
      // Handle filters from chart details (adhoc_filters) or legacy format
      if (chartDetails?.adhoc_filters && Array.isArray(chartDetails.adhoc_filters)) {
        // Convert adhoc_filters to our filter format
        const filterColumns: string[] = []
        const filterVals: { [key: string]: any } = {}
        
        // Group filters by subject to handle time ranges properly
        const filtersBySubject: { [key: string]: any[] } = {}
        chartDetails.adhoc_filters.forEach((filter: any) => {
          const columnName = filter.subject
          if (!filtersBySubject[columnName]) {
            filtersBySubject[columnName] = []
          }
          filtersBySubject[columnName].push(filter)
        })
        
        // Process each unique column
        Object.keys(filtersBySubject).forEach((columnName) => {
          filterColumns.push(columnName)
          const columnFilters = filtersBySubject[columnName]
          
          if (isTimeBasedFilter(columnName)) {
            // Handle time-based filters from adhoc_filters
            const startFilter = columnFilters.find((f: any) => f.operator === '>=')
            const endFilter = columnFilters.find((f: any) => f.operator === '<=')
            
            if (startFilter && endFilter) {
              const startMillis = startFilter.comparator
              const endMillis = endFilter.comparator
              const diffMillis = endMillis - startMillis
              const diffDays = Math.round(diffMillis / (1000 * 60 * 60 * 24))
              
              if (diffDays <= 1) {
                filterVals[columnName] = ['1 Day']
              } else if (diffDays <= 7) {
                filterVals[columnName] = ['1 Week']
              } else if (diffDays <= 31) {
                filterVals[columnName] = ['1 Month']
              } else if (diffDays <= 93) {
                filterVals[columnName] = ['1 Quarter']
              } else {
                filterVals[columnName] = ['Custom']
                setCustomDateRanges(prev => ({
                  ...prev,
                  [columnName]: {
                    start: new Date(startMillis).toISOString().split('T')[0],
                    end: new Date(endMillis).toISOString().split('T')[0]
                  }
                }))
              }
            }
          } else {
            // Handle regular filters - support both single values and arrays
            const inFilter = columnFilters.find((f: any) => f.operator === 'IN')
            const equalFilter = columnFilters.find((f: any) => f.operator === '==')
            
            if (inFilter && Array.isArray(inFilter.comparator)) {
              // Handle IN operator with array values (multiselect)
              filterVals[columnName] = inFilter.comparator
            } else if (equalFilter) {
              // Handle == operator with single value
              filterVals[columnName] = [equalFilter.comparator]
            }
          }
        })
      
        
        setSelectedFilterColumns(filterColumns)
        setFilterValues(filterVals)
        
        // Pre-load column options for the filters that have values
        filterColumns.forEach(async (columnName) => {
          if (filterVals[columnName] && !isTimeBasedFilter(columnName)) {
            // Set the options directly if we have the values from adhoc_filters
            if (Array.isArray(filterVals[columnName])) {
              const options = filterVals[columnName].map((value: any) => ({
                label: value,
                value: value
              }))
              setColumnOptions_Values(prev => ({
                ...prev,
                [columnName]: options
              }))
            }
          }
          // Always fetch column values for all filter columns to ensure dropdown options are populated
          await fetchColumnValues(columnName)
        })
      } else if (details.filters && Array.isArray(details.filters) && details.filters.length > 0) {
        // Handle legacy filter format
        const filterConditions = details.filters[0]?.conditions || []
        const filterColumns = filterConditions.map((condition: any) => condition.type)
        const filterVals: { [key: string]: any } = {}
        
        filterConditions.forEach((condition: any) => {
          if (isTimeBasedFilter(condition.type) && condition.conditionBy === '<>') {
            // For time-based filters, convert milliseconds back to readable format
            const [startMillis, endMillis] = condition.values
            const diffMillis = endMillis - startMillis
            const diffDays = Math.round(diffMillis / (1000 * 60 * 60 * 24))
            
            if (diffDays <= 1) {
              filterVals[condition.type] = ['1 Day']
            } else if (diffDays <= 7) {
              filterVals[condition.type] = ['1 Week']
            } else if (diffDays <= 31) {
              filterVals[condition.type] = ['1 Month']
            } else if (diffDays <= 93) {
              filterVals[condition.type] = ['1 Quarter']
            } else {
              filterVals[condition.type] = ['Custom']
              // Set custom date range
              setCustomDateRanges(prev => ({
                ...prev,
                [condition.type]: {
                  start: new Date(startMillis).toISOString().split('T')[0],
                  end: new Date(endMillis).toISOString().split('T')[0]
                }
              }))
            }
          } else {
            // For regular filters
            filterVals[condition.type] = condition.values
          }
        })
        
        setSelectedFilterColumns(filterColumns)
        setFilterValues(filterVals)
        
        // Fetch column values for all filter columns to ensure dropdown options are populated
        filterColumns.forEach(async (columnName: string) => {
          await fetchColumnValues(columnName)
        })
      }
      
      // Set form field values
      const formValues = {
        name: alertDetails?.name || details.alert_name || details.name || '',
        email: emailValue,
        columns: columnsFromChart || []
      }
      form.setFieldsValue(formValues)
    }
  }, [action, details])

    // Handle form submission
  const handleSubmit = async () => {
    
    try {
      if (action === 'edit') {
        // Edit/Update payload - includes alert_id and chart_id
        const editPayload: any = {
          alert_id: alert_id || details?.alert_id || details?.id,
          chart_id: chart_id || details?.chart_id,
          name: data.alert_name,
          recipients: [
            {
              recipient_config_json: {
                bccTarget: "",
                ccTarget: "",
                target: data.email
              },
              type: "Email"
            }
          ],
          schedule: cronExpression,
          columns: selectedColumns,
          filters: generateFiltersPayload(),
          active: true,
          email_subject: "",
          description: ""
        }
         
        await dispatchUpdateAlert(editPayload)
         
        // Refresh alerts list after successful update
        await dispatchGetAlertsForControlRoom({
          limit: 100,
          offset: 0
        })
         
        // Call the original callback to close modal or handle success
        onFormSubmission(editPayload)
      } else {
        // Create payload - standard creation format
        const createPayload = {
          name: data.alert_name,
          datasource_id: filterData?.datasource?.id,
          recipients: [
            {
              recipient_config_json: {
                bccTarget: "",
                ccTarget: "",
                target: data.email
              },
              type: "Email"
            }
          ],
          schedule: cronExpression,
          columns: selectedColumns,
          filters: generateFiltersPayload(),
          active: true,
          email_subject: "",
          description: ""
        }
         
        await dispatchCreateAlert(createPayload)
         
        // Refresh alerts list after successful creation
        await dispatchGetAlertsForControlRoom({
          limit: 100,
          offset: 0
        })
         
        // Call the original callback to close modal or handle success
        onFormSubmission(createPayload)
      }
    } catch (error) {
      console.error('Failed to submit alert:', error)
      // Error handling is done in Redux actions with notifications
      // Form will remain open so user can retry
    }
  }

  return (
    <StyledContainerWithBackground>
      <StyledForm
        name='alertform'
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        onFinishFailed={() => null}
        autoComplete='off'
      >
        <Form.Item
          name='name'
        // rules={[{ required: true, message: 'Please fill out this field' }]}
        >
          <InputContainer>
            <WizrText type='body2'>Alert Name</WizrText>
            <StyledWizrInput
              required={false}
              value={data?.alert_name}
              placeholder='Enter Alert Name'
              onChange={(value: string) => {
                setdata({ ...data, alert_name: value });
                form.setFieldsValue({ name: value });
              }}
            />
          </InputContainer>
        </Form.Item>

        <Form.Item
          name='email'
        // rules={[{ required: true, message: 'Please fill out this field' }]}
        >
          <InputContainer>
            <WizrText type='body2'>Email</WizrText>
            <StyledWizrInput
              required={false}
              value={data?.email}
              placeholder='Enter Email'
              onChange={(value: string) => {
                setdata({ ...data, email: value });
                form.setFieldsValue({ email: value });
              }}
            />
          </InputContainer>
        </Form.Item>

        <Form.Item>
          <InputContainer>
            <WizrText type='body2'>Schedule Interval (Every)</WizrText>
            <StyledDropDown
              value={scheduleInterval}
              options={scheduleIntervalOptions}
              onChange={(value: any) => {
                setScheduleInterval(value);
              }}
            />
          </InputContainer>
        </Form.Item>

        {scheduleInterval === 'year' && (
          <Form.Item>
            <InputContainer>
              <WizrText type='body2'>Month of Year</WizrText>
              <StyledDropDown
                value={scheduleConfig.month}
                options={monthOptions}
                onChange={(value: any) => {
                  setScheduleConfig({ ...scheduleConfig, month: value });
                }}
              />
            </InputContainer>
          </Form.Item>
        )}

        {(scheduleInterval === 'year' || scheduleInterval === 'month') && (
          <Form.Item>
            <InputContainer>
              <WizrText type='body2'>Day of Month</WizrText>
              <StyledDropDown
                value={scheduleConfig.dayOfMonth}
                options={dayOfMonthOptions}
                onChange={(value: any) => {
                  setScheduleConfig({ ...scheduleConfig, dayOfMonth: value });
                }}
              />
            </InputContainer>
          </Form.Item>
        )}

        {scheduleInterval === 'week' && (
          <Form.Item>
            <InputContainer>
              <WizrText type='body2'>On Day</WizrText>
              <StyledDropDown
                value={scheduleConfig.dayOfWeek}
                options={dayOfWeekOptions}
                onChange={(value: any) => {
                  setScheduleConfig({ ...scheduleConfig, dayOfWeek: value });
                }}
              />
            </InputContainer>
          </Form.Item>
        )}

        {(scheduleInterval === 'year' || scheduleInterval === 'month' || scheduleInterval === 'week' || scheduleInterval === 'day') && (
          <Form.Item>
            <InputContainer>
              <WizrText type='body2'>At Hour</WizrText>
              <StyledDropDown
                value={scheduleConfig.hour}
                options={hourOptions}
                onChange={(value: any) => {
                  setScheduleConfig({ ...scheduleConfig, hour: value });
                }}
              />
            </InputContainer>
          </Form.Item>
        )}

        {(scheduleInterval === 'year' || scheduleInterval === 'month' || scheduleInterval === 'week' || scheduleInterval === 'day' || scheduleInterval === 'hour') && (
          <Form.Item>
            <InputContainer>
              <WizrText type='body2'>At Minute</WizrText>
              <StyledDropDown
                value={scheduleConfig.minute}
                options={minuteOptions}
                onChange={(value: any) => {
                  setScheduleConfig({ ...scheduleConfig, minute: value });
                }}
              />
            </InputContainer>
          </Form.Item>
        )}

        <Form.Item
          name='columns'
        // rules={[{ required: true, message: 'Please fill out this field' }]}
        >
          <InputContainer>
            <WizrText type='body2'>Columns</WizrText>
            <StyledWizrTagInput
              mode='multiple'
              options={columnOptions}
              value={selectedColumns}
              onChange={(value: any) => {
                setSelectedColumns(value);
                form.setFieldsValue({ columns: value });
              }}
              maxTagCount={'responsive'}
              placeholder="Select columns"
            ></StyledWizrTagInput>
          </InputContainer>
        </Form.Item>

        <FiltersContainer>
          <WizrText type='sub1'>Filters</WizrText>
          <FilterPopover
            columnOptions={columnOptions}
            selectedFilters={selectedFilterColumns}
            onFiltersChange={handleFilterColumnsChange}
          />
        </FiltersContainer>

        {/* Dynamic Filter Fields */}
        {selectedFilterColumns.map((column) => {
          const isTimeBased = isTimeBasedFilter(column)
          const showCustomDatePicker = isTimeBased && filterValues[column]?.includes('Custom')
          
          return (
            <FilterFormItem key={column}>
              <InputContainer>
                <WizrText type='body2'>{column.replace(/_/g, ' ')}</WizrText>
                <StyledWizrTagInput
                  mode={isTimeBased ? undefined : 'multiple'} // Single selection for time filters
                  options={columnOptions_Values[column] || []}
                  value={Array.isArray(filterValues[column]) ? filterValues[column] : (filterValues[column] ? [filterValues[column]] : [])}
                  onChange={(values: any) => {
                    // For time-based filters, ensure single selection
                    if (isTimeBased) {
                      const singleValue = Array.isArray(values) ? values[0] : values
                      updateFilterValue(column, [singleValue])
                      
                      // Clear custom date range if not selecting Custom
                      if (singleValue !== 'Custom') {
                        setCustomDateRanges(prev => ({
                          ...prev,
                          [column]: { start: null, end: null }
                        }))
                      }
                    } else {
                      updateFilterValue(column, values)
                    }
                  }}
                  onFocus={() => fetchColumnValues(column)}
                  placeholder={`Select ${column.replace(/_/g, ' ').toLowerCase()} ${isTimeBased ? 'period' : 'values'}`}
                  maxTagCount={isTimeBased ? 1 : 'responsive'}
                  loading={loadingColumns[column]}
                  notFoundContent={loadingColumns[column] ? 'Loading...' : 'No options available'}
                />
                
                {/* Show date picker for Custom selection on time-based filters */}
                {showCustomDatePicker && (
                  <CustomDatePickerContainer>
                    <StyledDateRangePicker
                      onChange={(dates: any) => {
                        if (dates && Array.isArray(dates) && dates[0] && dates[1]) {
                          setCustomDateRanges(prev => ({
                            ...prev,
                            [column]: {
                              start: dates[0].format('YYYY-MM-DD'),
                              end: dates[1].format('YYYY-MM-DD')
                            }
                          }))
                        } else {
                          setCustomDateRanges(prev => ({
                            ...prev,
                            [column]: { start: null, end: null }
                          }))
                        }
                      }}
                      placeholder={['Start Date', 'End Date']}
                    />
                  </CustomDatePickerContainer>
                )}
              </InputContainer>
            </FilterFormItem>
          )
        })}


        <FormActionsContainer>
          <FormActionsFlexSpacing>
            <CancelButton
              type='default'
              text='Cancel'
              onClick={onCancel}
            />
            <SubmitButton
              htmlType='submit'
              text={action === 'edit' ? 'Update' : 'Save'}
              type='primary'
            />
          </FormActionsFlexSpacing>
        </FormActionsContainer>
      </StyledForm>
    </StyledContainerWithBackground>
  )
}

export default NewAlertForm